import {
  Button,
  Box,
  Card,
  CardContent,
  Sheet,
  Table,
  Typography,
} from "@mui/joy";
import { useTranslation } from "../../../../i18n";
import {
  InspectionResult,
  UpdateInspectionInput,
  useInspectionDetailsByRefQuery,
  useInspectionDetailsUpdateMutation,
} from "../../../../__generated__/types-and-hooks";
import QTextInput from "../../../../x-components/QTextInput";
import { useParams } from "react-router-dom";
import QSelect from "../../../../x-components/QSelect";
import { useInspectionResultOptions } from "../hooks/useInspectionResultOptions";
import { FilesCard } from "../../../../components/FilesCard";
import { InspectionPDF } from "./ExportToPdf";
import { pdf } from "@react-pdf/renderer";

async function downloadPDF(inspection: any) {
  const blob = await pdf(<InspectionPDF inspection={inspection} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `inspection-${inspection.uniqueId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Files() {
  const inspectionResultOptions = useInspectionResultOptions();
  const { ref } = useParams();
  const inspectionQuery = useInspectionDetailsByRefQuery({
    variables: { uniqueId: ref! },
  });
  const inspection = inspectionQuery.data?.inspection_by_unique_id;
  const t = useTranslation();
  const [inspectionUpdateMutation] = useInspectionDetailsUpdateMutation({});

  const updateFn = async (updates: Omit<UpdateInspectionInput, "uniqueId">) => {
    const res = await inspectionUpdateMutation({
      variables: {
        updates: {
          uniqueId: inspection?.uniqueId!,
          ...updates,
        },
      },
      onCompleted(data) {
        inspectionQuery.refetch();
      },
    });
    return res.errors?.map((err) => err.message).join("; ");
  };

  return (
    <Box
      sx={{
        p: 2,
        columns: "400px",
      }}
    >
      {inspection && (
        <Button
          onClick={() => {
            downloadPDF(inspection);
          }}
        >
          Download
        </Button>
      )}

      <Card
        sx={{
          backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
          padding: 0,
          mb: 2,
          breakInside: "avoid",
        }}
        variant="outlined"
      >
        <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
          {t("Final Report")}
        </Typography>
        <CardContent
          sx={{
            m: 0,
            margin: "2px",
            borderRadius: 5,
            border: "1px solid black",
            bgcolor: "background.surface",
            gap: 0,
            p: 1,
            pl: 2,
          }}
        >
          <Sheet sx={{ overflow: "auto" }}>
            <Table
              borderAxis="xBetween"
              sx={{
                p: 0,
                m: 0,
                "& tbody td:nth-child(1)": { width: 180 },
                "& tbody td": { pl: 0 },
              }}
            >
              <tbody>
                <tr>
                  <td>
                    <Typography level="body-sm">{t("Result")}</Typography>
                  </td>
                  <td>
                    <QSelect
                      val={inspection?.result ?? null}
                      options={inspectionResultOptions}
                      update={async (result) =>
                        updateFn({
                          result: result as InspectionResult,
                        })
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-sm">{t("Inspected by")}</Typography>
                  </td>
                  <td>
                    <QTextInput
                      val={inspection?.inspectedBy ?? ""}
                      update={(inspectedBy) =>
                        updateFn({
                          inspectedBy: inspectedBy,
                        })
                      }
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-sm">
                      {t("Inspector Notes")}
                    </Typography>
                  </td>
                  <td>
                    <QTextInput
                      val={inspection?.inspectorNotes ?? ""}
                      update={(inspectorNotes) =>
                        updateFn({
                          inspectorNotes: inspectorNotes,
                        })
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Sheet>
        </CardContent>
      </Card>
      {inspection && (
        <FilesCard entityId={inspection?.uniqueId} entityType="inspection" />
      )}
    </Box>
  );
}
